// File: typescript/apps/front-end/src/main/react/page/group-page/groupChatLink.ts
//
// Where the group page sends a member who wants to talk to the group.
//
// Design: designs/01-now/2026-08-13-group-chat-repositioning-design.md §5.1 (entry points) and §5.3
// (the quiet-group case). The group's conversation lives in Messages now; the group page keeps one
// control that points at it.
//
// ---------------------------------------------------------------------------------------------
// WHY THIS TAKES TWO KINDS OF ADDRESS, AND WHY THE GROUP-ID FORM IS NOT A SHORTCUT
// ---------------------------------------------------------------------------------------------
// The canonical address is the design's `/messages?threadPublicId=gc_<uuid>`, where the uuid is
// `group_board.public_id`. The group page CANNOT build that today, and the reason is structural
// rather than an omission:
//
//   * `group_board` rows are created lazily, on first post (`GroupBoardService`), so 502 of 520
//     groups have no board row at all and therefore no uuid to address;
//   * the group page's bootstrap (`group.jte` -> `mountGroupPage`) carries no board identifier, and
//     the only endpoint that returns one is `getGroupBoard` — the per-page-load fetch this very
//     slice exists to delete (design §5.2). Replacing one fetch with another fetch is the defect
//     restated, so the id is not fetched;
//   * §5.3 is precisely the quiet group with no board and no Messages row. If this control only
//     worked once a board existed, it would be dead in exactly the case it was built for.
//
// So the group-scoped form is what the group page actually emits: the destination is named by the
// GROUP, and Messages resolves it to (and, on first open, materializes) the conversation. The
// thread-scoped form is kept because every OTHER entry point — a Messages row, a notification deep
// link — already holds a real `gc_<uuid>` and must keep using the canonical address.
//
// Note this module never writes the `gc_` prefix itself. A `threadPublicId` handed to it is one the
// server minted (`GroupChatThreadId.of`), carried verbatim; the namespace lives in one place on each
// side of the wire, which is the point of having it.

/**
 * The address of a group conversation in Messages, or null when neither identifier is available.
 *
 * <p>`threadPublicId` wins when both are present: it names the conversation itself, while
 * `groupPublicId` only names the room it belongs to.
 *
 * <p>Returns a plain path, deliberately. It is consumed as an `href` on a real anchor so the app's
 * own anchor-click handler (`EnhancedNavigation`) performs the navigation through the single
 * sanctioned chokepoint — no `history.pushState`, no entry built by spreading the current one
 * (AGENTS.md rule 33).
 */
export function buildGroupChatUrl(options: {
    threadPublicId?: string | null;
    groupPublicId?: string | null;
}): string | null {
    const threadPublicId = String(options.threadPublicId ?? "").trim();
    if (threadPublicId) {
        return `/messages?threadPublicId=${encodeURIComponent(threadPublicId)}`;
    }

    const groupPublicId = String(options.groupPublicId ?? "").trim();
    if (groupPublicId) {
        return `/messages?groupPublicId=${encodeURIComponent(groupPublicId)}`;
    }

    return null;
}
